import React, {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
  ComponentType,
} from "react";

/**
 * A higher-order component that wraps a component with React.forwardRef if it isn't already.
 * This is useful for components that might be used with TouchableWithoutFeedback or other
 * components that forward refs.
 *
 * @param Component - The component to wrap
 * @returns A component that can accept refs
 */
export function withForwardRef<T, P extends object = object>(
  Component: ComponentType<P>,
): ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>> {
  if (
    typeof Component === "object" &&
    (Component as { $$typeof?: symbol }).$$typeof ===
      Symbol.for("react.forward_ref")
  ) {
    return Component as ForwardRefExoticComponent<
      PropsWithoutRef<P> & RefAttributes<T>
    >;
  }

  const WithForwardRef = forwardRef<T, P>((props, ref) => (
    <Component {...(props as P)} />
  ));

  WithForwardRef.displayName = `withForwardRef(${
    Component.displayName || Component.name || "Component"
  })`;

  return WithForwardRef;
}
